import React, { useEffect, useRef } from 'react';
import { TherapistServicesProps } from '../../types';

/**
 * Componente Services renovado con diseño innovador y vanguardista
 */
const TherapistServices: React.FC<TherapistServicesProps> = ({ services, pricing, therapist }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Función para generar mensaje de WhatsApp personalizado
  const generateWhatsAppMessage = (serviceName: string, whatsappNumber?: string) => {
    const message = `Hola, me interesa conocer más información sobre ${serviceName}. ¿Podrías brindarme detalles sobre el proceso terapéutico, metodología y disponibilidad de horarios? Gracias.`;
    const phoneNumber = whatsappNumber?.replace(/\D/g, '') || "56978009119";
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  useEffect(() => {
    // Configurar el Intersection Observer para animaciones scroll-triggered
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observar todos los elementos animables
    const animatableElements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    animatableElements?.forEach(el => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Función para obtener color por tipo de servicio — paleta WCAG AA del sistema de diseño
  const getServiceColor = (serviceId: string): string => {
    const colors: Record<string, string> = {
      'individual': '#1A6FBF',
      'couple':     '#C05E00',
      'family':     '#057A55',
      'sexology':   '#6B21A8',
      'trauma':     '#1A6FBF',
      'evaluation': '#1E4A8F',
    };
    return colors[serviceId] ?? '#1E4A8F';
  };

  return (
    <section ref={sectionRef} id="services" className="therapist-services-modern">
      <div className="container">
        {/* Header moderno */}
        <div className="modern-section-header animate-on-scroll">
          <div className="header-decoration"></div>
          <h2 className="modern-section-title">
            <span className="title-highlight">Servicios</span> Especializados
          </h2>
          <p className="modern-section-subtitle">
            Atención personalizada con metodologías probadas y enfoque integral
          </p>
        </div>

        {/* Grid de servicios modernos */}
        <div className="services-modern-grid animate-on-scroll">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className={`service-card-modern ${service.id}`}
              style={{ '--delay': `${index * 0.1}s`, '--service-color': getServiceColor(service.id) } as React.CSSProperties}
            >
              {/* Header de la card */}
              <div className="service-card-header">
                <div className="service-icon-modern">
                  <i className={service.categoryIcon}></i>
                </div>
                <div className="service-main-info">
                  <h3 className="service-name-modern">{service.name}</h3>
                  <div className="service-meta">
                    <span className="service-price-modern">{service.price}</span>
                    <span className="service-duration-modern">
                      <i className="fas fa-clock"></i>
                      {service.duration}
                    </span>
                  </div>
                </div>
              </div>

              {/* Descripción compacta */}
              <div className="service-description-modern">
                <p>{service.description}</p>
              </div>

              {/* Modalidades modernas */}
              <div className="service-modalities-modern">
                <div className="modalities-label">Modalidades:</div>
                <div className="modalities-badges">
                  {service.modalities.map((modality, i) => (
                    <span key={i} className={`modality-badge-modern ${modality}`}>
                      <i className={
                        modality === 'presencial' ? 'fas fa-building' :
                        modality === 'online' ? 'fas fa-video' :
                        'fas fa-sync-alt'
                      }></i>
                      {modality === 'presencial' ? 'Presencial' :
                       modality === 'online' ? 'Online' :
                       'Híbrido'}
                    </span>
                  ))}
                </div>
              </div>

              {/* Lista de incluidos compacta */}
              {service.included && service.included.length > 0 && (
                <div className="service-includes-modern">
                  <div className="includes-label">Incluye:</div>
                  <div className="includes-grid">
                    {service.included.map((item, i) => (
                      <div key={i} className="include-item">
                        <i className="fas fa-check-circle"></i>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer con CTAs modernos */}
              <div className="service-footer-modern">
                <button className="btn-modern btn-primary-modern">
                  <i className="fas fa-calendar-plus"></i>
                  <span>Agendar</span>
                </button>
                <a 
                  href={generateWhatsAppMessage(service.name, therapist.socialMedia?.whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-modern btn-outline"
                >
                  <i className="fab fa-whatsapp"></i>
                  <span>Consultar</span>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Comparación de precios moderna */}
        <div className="pricing-comparison-modern animate-on-scroll">
          <div className="pricing-header-modern">
            <h3 className="pricing-title-modern">Comparación de Precios</h3>
            <p className="pricing-subtitle-modern">Tarifas unificadas con ofertas especiales</p>
          </div>
          
          <div className="pricing-cards-grid">
            <div className="pricing-card individual">
              <div className="pricing-card-header">
                <i className="fas fa-user"></i>
                <h4>Terapia Individual</h4>
              </div>
              <div className="pricing-options">
                <div className="price-option">
                  <span className="price-label">Presencial/Online</span>
                  <span className="price-value">{pricing.individual.presencial}</span>
                </div>
                {pricing.individual.promo && pricing.individual.promoPrice && (
                  <div className="price-option promo">
                    <span className="price-label">{pricing.individual.promo}</span>
                    <span className="price-value">{pricing.individual.promoPrice}</span>
                    <span className="promo-tag">{pricing.individual.savings}</span>
                  </div>
                )}
              </div>
            </div>

            {pricing.couple && (
              <div className="pricing-card couple">
                <div className="pricing-card-header">
                  <i className="fas fa-heart"></i>
                  <h4>Terapia de Pareja</h4>
                </div>
                <div className="pricing-options">
                  <div className="price-option">
                    <span className="price-label">Presencial/Online</span>
                    <span className="price-value">{pricing.couple.presencial}</span>
                  </div>
                  {pricing.couple.special && pricing.couple.specialPrice && (
                    <div className="price-option special">
                      <span className="price-label">{pricing.couple.special}</span>
                      <span className="price-value">{pricing.couple.specialPrice}</span>
                      <span className="special-tag">{pricing.couple.savings}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {pricing.family && (
              <div className="pricing-card family">
                <div className="pricing-card-header">
                  <i className="fas fa-users"></i>
                  <h4>Terapia Familiar</h4>
                </div>
                <div className="pricing-options">
                  <div className="price-option">
                    <span className="price-label">Presencial</span>
                    <span className="price-value">{pricing.family.presencial}</span>
                  </div>
                  {pricing.family.doubleSession && pricing.family.doublePrice && (
                    <div className="price-option double">
                      <span className="price-label">{pricing.family.doubleSession}</span>
                      <span className="price-value">{pricing.family.doublePrice}</span>
                      <span className="value-tag">{pricing.family.savings}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Información adicional moderna */}
        <div className="services-features-modern animate-on-scroll">
          <h3 className="features-title">Garantías y Beneficios</h3>
          <div className="features-grid-innovative">
            <div className="feature-item-innovative">
              <div className="feature-visual-container">
                <div className="feature-icon-innovative time">
                  <div className="icon-background"></div>
                  <i className="fas fa-stopwatch"></i>
                  <div className="icon-pulse"></div>
                </div>
              </div>
              <div className="feature-content-innovative">
                <h4>Duración Flexible</h4>
                <p>45-50 minutos adaptados a tus necesidades</p>
                <span className="feature-badge">Personalizado</span>
              </div>
            </div>

            <div className="feature-item-innovative">
              <div className="feature-visual-container">
                <div className="feature-icon-innovative security">
                  <div className="icon-background"></div>
                  <i className="fas fa-user-shield"></i>
                  <div className="icon-pulse"></div>
                </div>
              </div>
              <div className="feature-content-innovative">
                <h4>Confidencialidad Total</h4>
                <p>Código de ética profesional estricto</p>
                <span className="feature-badge">100% Seguro</span>
              </div>
            </div>

            <div className="feature-item-innovative">
              <div className="feature-visual-container">
                <div className="feature-icon-innovative invoice">
                  <div className="icon-background"></div>
                  <i className="fas fa-receipt"></i>
                  <div className="icon-pulse"></div>
                </div>
              </div>
              <div className="feature-content-innovative">
                <h4>Boletas Reembolsables</h4>
                <p>Válidas para Isapre y seguros complementarios</p>
                <span className="feature-badge">Certificado</span>
              </div>
            </div>

            <div className="feature-item-innovative">
              <div className="feature-visual-container">
                <div className="feature-icon-innovative emergency">
                  <div className="icon-background"></div>
                  <i className="fas fa-headset"></i>
                  <div className="icon-pulse"></div>
                </div>
              </div>
              <div className="feature-content-innovative">
                <h4>Apoyo de Emergencia</h4>
                <p>Disponibilidad para crisis inmediatas</p>
                <span className="feature-badge">24/7</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TherapistServices;